/**
 * Monthly Report fixture data — person-level rows with expandable
 * client/project sub-rows. Modelled after the ReportMonthly skill-test
 * prototype (hierarchical accordion table).
 */

export interface MonthlyProjectRow {
  /** Task 156 D3 — server-side project id, when known. Mockup data leaves
   *  this `undefined`, which renders the project name as plain text instead
   *  of a `<Link>`. */
  id?: string;
  name: string;
  hours: string;
  billable: string | null;
  general: string | null;
  events: string;
}

export interface MonthlyClientGroup {
  /** Task 156 D3 — server-side client id, when known. `undefined` for the
   *  fixture/mockup data and for the system-owned `Internal / Not Set`
   *  bucket; both render as plain text rather than a link. */
  id?: string;
  name: string | null; // null = General (non-billable)
  projects: readonly MonthlyProjectRow[];
}

export interface MonthlyPersonRow {
  id: string;
  name: string;
  hours: string;
  billable: string;
  general: string;
  events: string;
  pct: number;
  collapsedHint?: string;
  clients?: readonly MonthlyClientGroup[];
}

export const MONTHLY_STATS = [
  { label: 'Total Hours', value: '847.5h' },
  { label: 'Billable Hours', value: '612.0h' },
  { label: 'General Hours', value: '235.5h' },
  { label: 'Team Members', value: '8' },
] as const;

export const MONTHLY_PEOPLE: readonly MonthlyPersonRow[] = [
  {
    id: 'sarah-chen',
    name: 'Sarah Chen',
    hours: '142.5h',
    billable: '118.2h',
    general: '24.3h',
    events: '1,710',
    pct: 17,
    clients: [
      {
        name: 'Meridian Capital',
        projects: [
          {
            name: 'Corporate Restructuring',
            hours: '62.3h',
            billable: '62.3h',
            general: null,
            events: '748',
          },
        ],
      },
      {
        name: 'TechVenture Labs',
        projects: [
          {
            name: 'Patent Portfolio Review',
            hours: '35.8h',
            billable: '35.8h',
            general: null,
            events: '430',
          },
        ],
      },
      {
        name: 'Pinnacle Financial',
        projects: [
          {
            name: 'Regulatory Compliance',
            hours: '20.1h',
            billable: '20.1h',
            general: null,
            events: '241',
          },
        ],
      },
      {
        name: null,
        projects: [
          {
            name: 'Internal Training',
            hours: '14.2h',
            billable: null,
            general: '14.2h',
            events: '170',
          },
          {
            name: 'Firm Administration',
            hours: '10.1h',
            billable: null,
            general: '10.1h',
            events: '121',
          },
        ],
      },
    ],
  },
  {
    id: 'marcus-rivera',
    name: 'Marcus Rivera',
    hours: '128.0h',
    billable: '102.4h',
    general: '25.6h',
    events: '1,536',
    pct: 15,
    clients: [
      {
        name: 'Apex Holdings',
        projects: [
          {
            name: 'M&A Due Diligence',
            hours: '52.0h',
            billable: '52.0h',
            general: null,
            events: '624',
          },
          {
            name: 'Board Governance',
            hours: '18.5h',
            billable: '18.5h',
            general: null,
            events: '222',
          },
        ],
      },
      {
        name: 'Meridian Capital',
        projects: [
          {
            name: 'Corporate Restructuring',
            hours: '31.9h',
            billable: '31.9h',
            general: null,
            events: '383',
          },
        ],
      },
      {
        name: null,
        projects: [
          {
            name: 'Firm Administration',
            hours: '15.2h',
            billable: null,
            general: '15.2h',
            events: '182',
          },
          {
            name: 'Internal Training',
            hours: '10.4h',
            billable: null,
            general: '10.4h',
            events: '125',
          },
        ],
      },
    ],
  },
  {
    id: 'aisha-patel',
    name: 'Aisha Patel',
    hours: '119.8h',
    billable: '95.8h',
    general: '24.0h',
    events: '1,438',
    pct: 14,
    collapsedHint: '\u25B6 3 clients \u00B7 5 projects',
  },
  {
    id: 'james-obrien',
    name: 'James O\u2019Brien',
    hours: '112.5h',
    billable: '88.0h',
    general: '24.5h',
    events: '1,350',
    pct: 13,
    collapsedHint: '\u25B6 2 clients \u00B7 4 projects',
  },
  {
    id: 'wei-zhang',
    name: 'Wei Zhang',
    hours: '108.2h',
    billable: '82.5h',
    general: '25.7h',
    events: '1,298',
    pct: 13,
    collapsedHint: '\u25B6 3 clients \u00B7 6 projects',
  },
  {
    id: 'elena-kowalski',
    name: 'Elena Kowalski',
    hours: '98.5h',
    billable: '72.3h',
    general: '26.2h',
    events: '1,182',
    pct: 12,
    collapsedHint: '\u25B6 2 clients \u00B7 3 projects',
  },
  {
    id: 'david-kim',
    name: 'David Kim',
    hours: '72.0h',
    billable: '48.0h',
    general: '24.0h',
    events: '864',
    pct: 8,
    collapsedHint: '\u25B6 1 client \u00B7 3 projects',
  },
  {
    id: 'lisa-thompson',
    name: 'Lisa Thompson',
    hours: '66.0h',
    billable: '4.8h',
    general: '61.2h',
    events: '793',
    pct: 8,
    collapsedHint: '\u25B6 2 clients \u00B7 4 projects',
  },
];
